/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { ProgressBar } from 'primeng/progressbar';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { FileSelectEvent, FileUpload } from 'primeng/fileupload';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { SharedModule } from 'primeng/api';
import { IngestionApiService } from '../../../services/ingestion-api.service';
import { StatusEnumDocument } from '../../../enum/status-document.enum';
import { Document } from '../../../models/document.model';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UploadDialogDocumentComponent } from '../upload-dialog-document/upload-dialog-document.component';

@Component({
  selector: 'app-upload-zone',
  imports: [
    ProgressBar,
    ButtonModule,
    ToastModule,
    FileUpload,
    BadgeModule,
    OverlayBadgeModule,
    CommonModule,
    SharedModule,
    DynamicDialogModule,
  ],
  templateUrl: './upload-zone.component.html',
  styleUrls: ['./upload-zone.component.scss'],
  providers: [MessageService, DialogService],
  standalone: true,
})
export class UploadZoneComponent {
  constructor() {}

  files: File[] = [];
  ref: DynamicDialogRef | undefined;

  totalSize: number = 0;
  totalSizePercent: number = 0;
  readonly MAX_FILE_SIZE = 100000000;
  private messageService = inject(MessageService);
  private dialogService = inject(DialogService);
  private config = inject(PrimeNG);
  private ingestionApiService = inject(IngestionApiService);

  @ViewChild('fileUploadRef') fileUploadRef!: FileUpload;

  isUploading: boolean = false;
  uploadProgress: number = 0;

  @Input() fileDropped: (file: File) => void = () => {};
  @Output() fileUploaded = new EventEmitter();

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileDropped(input.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.fileDropped(event.dataTransfer.files[0]);
    }
  }

  formatSize(bytes: number) {
    const k = 1024;
    const dm = 3;
    const sizes = this.config.translation.fileSizeTypes;
    if (bytes === 0) {
      return `0 ${sizes![0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes![i]}`;
  }

  choose($event: MouseEvent, callback: () => void) {
    callback();
  }

  uploadEvent(callback: () => void) {
    callback();
  }

  async onTemplatedUpload(event: any) {
    const uploadedFile = event.files[0];

    if (!uploadedFile) return;

    this.ref = this.dialogService.open(UploadDialogDocumentComponent, {
      header: 'Document MetaData',
      width: '50vw',
      contentStyle: { overflow: 'auto' },
      data: uploadedFile,
    });

    this.ref.onClose.subscribe(async (dialogData: any) => {
      if (!dialogData) {
        this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Upload cancelled by the user.' });

        if (this.fileUploadRef) this.fileUploadRef.clear();
        return;
      }

      try {
        const documentPayload: Document = {
          document_id: dialogData.document_id,
          metadata: {
            category: dialogData.category,
            description: dialogData.description,
            keywords: dialogData.keywords,
            latest: dialogData.latest ? '1' : '0',
            link: dialogData.link,
            year_of_publication: dialogData.year_of_publication,
            timestamp:
              dialogData.timestamp instanceof Date ? dialogData.timestamp.toISOString() : new Date().toISOString(),
          },
          updated_at: new Date().toISOString(),
          status: StatusEnumDocument.UPLOADING,
        };

        const { presigned_url } = await this.ingestionApiService.uploadDocument(documentPayload, uploadedFile.type);
        console.log('presigned_url', presigned_url);

        if (presigned_url) {
          this.isUploading = true;
          this.uploadProgress = 0;

          const progressInterval = setInterval(() => {
            if (this.uploadProgress < 90) {
              this.uploadProgress += Math.floor(Math.random() * 8) + 3;

              if (this.uploadProgress > 90) this.uploadProgress = 90;
            }
          }, 500);

          try {
            const uploadResponse = await fetch(presigned_url, {
              method: 'PUT',
              body: uploadedFile,
              headers: {
                'Content-Type': uploadedFile.type,
              },
            });

            clearInterval(progressInterval);

            if (uploadResponse.ok) {
              this.uploadProgress = 100;

              this.messageService.add({
                severity: 'success',
                summary: 'Upload Complete',
                detail: `File ${uploadedFile.name} successfully uploaded.`,
              });

              setTimeout(() => {
                this.resetUploadState();
                this.fileUploaded.emit();
              }, 1000);
            } else {
              clearInterval(progressInterval);
              this.handleUploadError();
            }
          } catch (error) {
            clearInterval(progressInterval);
            this.handleUploadError();
          }
        }
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Upload Error',
          detail: `Unable to initialize the upload for ${uploadedFile.name}.`,
        });
        console.error(error);
        this.resetUploadState();
      }
    });
  }

  private handleUploadError() {
    this.resetUploadState();
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error during upload to storage.',
    });
  }

  private resetUploadState() {
    this.isUploading = false;
    this.uploadProgress = 0;
    this.files = [];
    this.totalSize = 0;
    this.totalSizePercent = 0;
    if (this.fileUploadRef) {
      this.fileUploadRef.clear();
    }
  }

  onRemoveTemplatingFile(event: MouseEvent, file: any, removeFileCallback: any, index: number) {
    removeFileCallback(event, index);
    this.totalSize -= file.size;
    this.totalSizePercent = (this.totalSize / this.MAX_FILE_SIZE) * 100;
  }

  errorUploadingFile($event: any) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load file.',
    });
    console.log($event);
  }
}
